import { decodeAudioBuffer } from '@scale/frontend-lib/audio/decodeAudio';
import { Banner, Button, Icon, Modal, Typography } from '@scale/scaleui';
import { memo, useState } from 'react';
import { ChatAudioEditor } from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/audio-attachments/ChatAudioEditor';
import { ChatAudioRecorder } from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/audio-attachments/ChatAudioRecorder';
import { ChatAudioTranscript } from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/audio-attachments/ChatAudioTranscript';
import { useChatThemeStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useChatThemeStore';

const getAudioDurationFromBlob = async (blob: Blob): Promise<number | null> => {
  if (blob.size === 0) return 0;

  const audioBuffer = await decodeAudioBuffer(await blob.arrayBuffer());
  return audioBuffer?.duration ?? null;
};

type RecordedAudioData = { blob: Blob; duration?: number };

type Props = {
  currentResponseBlob: Blob | null;
  currentResponseUrl: string | null;
  onFinish: (args: {
    recordedAudio: RecordedAudioData | null;
    shouldResetHistory: boolean;
  }) => void;

  enableTranscript?: boolean;
  minAudioDuration?: number;
  maxAudioDuration?: number;
  isTextCollectionStep?: boolean;
};

export const ChatAudioEditableCombined = memo(
  ({
    currentResponseBlob,
    currentResponseUrl,
    onFinish,
    enableTranscript,
    minAudioDuration,
    maxAudioDuration,
    isTextCollectionStep,
  }: Props) => {
    const theme = useChatThemeStore(state => state.theme);

    const [editingAudio, setEditingAudio] = useState<RecordedAudioData | null>(null);
    const editingAudioBlob = editingAudio?.blob ?? null;
    const trimmedAudioDuration = editingAudio?.duration ?? null;

    const [shouldResetHistory, setShouldResetHistory] = useState(false);
    const [timeElapsed, setTimeElapsed] = useState<number>(0);

    // The duration of the audio after trimming and before saving in the editing modal
    const showExceedsDurationWarning =
      maxAudioDuration != null &&
      trimmedAudioDuration != null &&
      trimmedAudioDuration > maxAudioDuration;

    // recording
    if (currentResponseBlob === null && editingAudioBlob === null) {
      return (
        <div className="flex flex-col gap-4">
          <header className="flex gap-2 items-center">
            <Typography.Overline2>Record audio</Typography.Overline2>
          </header>

          <ChatAudioRecorder
            className="p-2"
            onFinish={async blob => {
              const duration = await getAudioDurationFromBlob(blob);
              setEditingAudio({ blob, duration: duration ?? undefined });
            }}
            minAudioDuration={minAudioDuration}
            maxAudioDuration={maxAudioDuration}
            timeElapsed={timeElapsed}
            setTimeElapsed={setTimeElapsed}
          />
        </div>
      );
    }

    // editing / playing
    return (
      <div className="flex flex-col gap-4">
        <header className="flex gap-2 items-center justify-between">
          <div className="flex gap-2 items-center">
            <Typography.Overline2>Record audio</Typography.Overline2>
            <Icon
              icon={['fas', 'undo']}
              size="md"
              tooltip="Delete recording and try again"
              onClick={() => {
                if (
                  !isTextCollectionStep &&
                  !window.confirm(
                    'Performing this action will wipe the turn history after this point. Are you sure you want to continue?',
                  )
                ) {
                  return;
                }
                onFinish({ recordedAudio: null, shouldResetHistory: false }); // resetting history is done on save
                setEditingAudio(null);
                setTimeElapsed(0);
                setShouldResetHistory(true);
              }}
            />
          </div>
          <div className="flex gap-2 items-center">
            <Button
              onClick={async () => {
                if (currentResponseBlob === null) {
                  setEditingAudio(null);
                } else {
                  const duration = await getAudioDurationFromBlob(currentResponseBlob);
                  setEditingAudio({
                    blob: currentResponseBlob,
                    duration: duration ?? undefined,
                  });
                }
                setShouldResetHistory(true); // Clicking "edit" is only possible after the audio has already been saved (i.e. submitted as the prompt)
              }}
              variant="secondary"
            >
              Edit
            </Button>
          </div>
        </header>

        {currentResponseBlob !== null && currentResponseUrl !== null && (
          <>
            <ChatAudioEditor originalBlob={currentResponseBlob} readonly />
            {enableTranscript && (
              <ChatAudioTranscript audioUrl={currentResponseUrl} userGeneratedAudio={true} />
            )}
          </>
        )}

        {editingAudioBlob !== null && (
          <Modal
            onClose={() => {
              setEditingAudio(null);
              setShouldResetHistory(false); // closing out of the modal should not trigger a reset
            }}
            theme={theme}
            title={currentResponseBlob === null ? 'Check audio before saving' : 'Edit audio'}
            open
            actions={
              <Button
                onClick={() => {
                  onFinish({
                    recordedAudio: editingAudio,
                    shouldResetHistory,
                  });
                  setEditingAudio(null);
                }}
                variant="primary"
              >
                Save
              </Button>
            }
          >
            {showExceedsDurationWarning && (
              <Banner
                title={`This recording exceeds the recommended time limit of ${maxAudioDuration} seconds.`}
                background="white"
                variant="danger"
              />
            )}
            <ChatAudioEditor
              originalBlob={editingAudioBlob}
              onFinish={async newBlob => {
                const duration = await getAudioDurationFromBlob(newBlob);
                setEditingAudio({ blob: newBlob, duration: duration ?? undefined });
              }}
              width={700}
            />
          </Modal>
        )}
      </div>
    );
  },
);
